/**
 * GW Tooltip Component
 * A reusable tooltip component that can be applied to any element.
 *
 * Usage:
 * <gw-tooltip text="Tooltip text" position="top|right|bottom|left">
 *   <button>Button with tooltip</button>
 * </gw-tooltip>
 */

class GWTooltip extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    this.tooltipElement = null;
    this.showTimeout = null;
    this.hideTimeout = null;

    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
  }

  static get observedAttributes() {
    return ['text', 'position', 'disabled'];
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'text' && this.tooltipElement) {
        this.tooltipElement.textContent = newValue || '';

        // If text is empty or removed, ensure tooltip is hidden
        if (!newValue) {
          this.hideTooltip(true); // Force immediate hide
        }
      } else if (name === 'position' && this.tooltipElement) {
        this.updateTooltipPosition();
      } else if (name === 'disabled') {
        this.updateDisabledState();
      }
    }
  }

  render() {
    const style = document.createElement('style');
    style.textContent = `
      :host {
        position: relative;
        display: inline-block;
      }
      
      .tooltip {
        width: 150px;
        position: absolute;
        background-color: white;
        color: var(--color-text, #262626);
        padding: 6px 10px;
        border-radius: 4px;
        font-size: 12px;
        line-height: 1.4;
        z-index: 10000;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        border: 1px solid var(--color-border, #eaeaea);
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease;
        pointer-events: none;
        max-width: 200px;
        text-align: center;
      }
      
      .tooltip--visible {
        opacity: 1;
        visibility: visible;
      }
      
      .tooltip--top {
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(-8px);
        margin-bottom: 8px;
      }
      
      .tooltip--top.tooltip--visible {
        transform: translateX(-50%) translateY(0);
      }
      
      .tooltip--right {
        left: 100%;
        top: 50%;
        transform: translateY(-50%) translateX(-8px);
        margin-left: 8px;
      }
      
      .tooltip--right.tooltip--visible {
        transform: translateY(-50%) translateX(0);
      }
      
      .tooltip--bottom {
        top: 100%;
        left: 50%;
        transform: translateX(-50%) translateY(-8px);
        margin-top: 8px;
      }
      
      .tooltip--bottom.tooltip--visible {
        transform: translateX(-50%) translateY(0);
      }
      
      .tooltip--left {
        right: 100%;
        top: 50%;
        transform: translateY(-50%) translateX(8px);
        margin-right: 8px;
      }
      
      .tooltip--left.tooltip--visible {
        transform: translateY(-50%) translateX(0);
      }
      
      .tooltip::after {
        content: '';
        position: absolute;
        border-style: solid;
        border-width: 5px;
      }
      
      .tooltip--top::after {
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-color: white transparent transparent transparent;
      }
      
      .tooltip--right::after {
        top: 50%;
        right: 100%;
        margin-top: -5px;
        border-color: transparent white transparent transparent;
      }
      
      .tooltip--bottom::after {
        bottom: 100%;
        left: 50%;
        margin-left: -5px;
        border-color: transparent transparent white transparent;
      }
      
      .tooltip--left::after {
        top: 50%;
        left: 100%;
        margin-top: -5px;
        border-color: transparent transparent transparent white;
      }
      
      /* Pass mouse events through to the wrapped content */
      :host ::slotted(*) {
        display: inline-block;
      }
    `;

    this.shadowRoot.innerHTML = '';
    this.shadowRoot.appendChild(style);

    const slot = document.createElement('slot');
    this.shadowRoot.appendChild(slot);

    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'tooltip';
    this.tooltipElement.textContent = this.getAttribute('text') || '';
    this.shadowRoot.appendChild(this.tooltipElement);

    this.updateTooltipPosition();
    this.updateDisabledState();
  }

  setupEventListeners() {
    if (this.hasAttribute('disabled')) return;

    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focus', this.handleFocus, true);
    this.addEventListener('blur', this.handleBlur, true);
  }

  removeEventListeners() {
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focus', this.handleFocus, true);
    this.removeEventListener('blur', this.handleBlur, true);
  }

  updateTooltipPosition() {
    if (!this.tooltipElement) return;

    const position = this.getAttribute('position') || 'top';
    this.tooltipElement.className = `tooltip tooltip--${position}`;
  }

  updateDisabledState() {
    const isDisabled = this.hasAttribute('disabled');

    if (isDisabled) {
      this.removeEventListeners();
      this.hideTooltip(true); // Force immediate hide
    } else {
      this.setupEventListeners();
    }
  }

  showTooltip() {
    // Don't show if disabled or no text provided
    if (this.hasAttribute('disabled') || !this.getAttribute('text')) return;

    clearTimeout(this.hideTimeout);

    this.showTimeout = setTimeout(() => {
      if (this.tooltipElement) {
        this.tooltipElement.classList.add('tooltip--visible');
      }
    }, 200);
  }

  hideTooltip(immediate = false) {
    clearTimeout(this.showTimeout);

    const hideAction = () => {
      if (this.tooltipElement) {
        this.tooltipElement.classList.remove('tooltip--visible');
      }
    };

    if (immediate) {
      hideAction();
    } else {
      this.hideTimeout = setTimeout(hideAction, 200);
    }
  }

  handleMouseEnter() {
    // Don't show tooltips for elements with disabled children
    const slottedElements = this.querySelectorAll('*');
    const hasDisabledChild = Array.from(slottedElements).some((el) => el.hasAttribute('disabled'));

    if (!hasDisabledChild || this.getAttribute('text')) {
      this.showTooltip();
    }
  }

  handleMouseLeave() {
    this.hideTooltip();
  }

  handleFocus() {
    this.showTooltip();
  }

  handleBlur() {
    this.hideTooltip();
  }
}

customElements.define('gw-tooltip', GWTooltip);
